import { useState } from 'react';
import { cn } from '@centraoneplus/ui';
import type {
  SuggestionItem,
  SimilarTicket,
  RecommendedArticle,
  ResponseTone,
} from '../../../services/ai.service';

export interface ResponseSuggestionsProps {
  suggestions: SuggestionItem[];
  similarTickets?: SimilarTicket[];
  recommendedArticles?: RecommendedArticle[];
  isLoading?: boolean;
  error?: string;
  onSelectSuggestion?: (suggestion: SuggestionItem) => void;
  onViewArticle?: (article: RecommendedArticle) => void;
  onViewTicket?: (ticket: SimilarTicket) => void;
  onRefresh?: (tone?: ResponseTone) => void;
  selectedTone?: ResponseTone;
  className?: string;
}

/**
 * Ivory & Brass suggested-reply strip — phase 6.
 *
 * The panel is gone: no header bar, no gradient, no card, no Sparkles. AI
 * provenance is carried by the SUGGESTED REPLY label alone. Suggestions are a
 * row of equal-width tiles; the first two are shown and the rest sit behind a
 * mono "+N MORE", because three-plus stacked cards was the old panel's bulk.
 */

const TONE_LABELS: Record<ResponseTone, string> = {
  professional: 'Professional',
  empathetic: 'Empathetic',
  apologetic: 'Apologetic',
  enthusiastic: 'Enthusiastic',
  technical: 'Technical',
};

const VISIBLE_BY_DEFAULT = 2;

/**
 * Renders ticket/order references (#1042, INV-88231) in the mono brass voice so
 * an identifier is scannable inside a wall of suggested prose.
 */
function withReferences(text: string) {
  const parts = text.split(/(\b[A-Z]{2,4}-\d{3,6}\b)/g);
  return parts.map((part, i) =>
    /^[A-Z]{2,4}-\d{3,6}$/.test(part) ? (
      <span
        key={i}
        className="font-mono text-[11.5px] text-[var(--co-brass-ink)]"
      >
        {part}
      </span>
    ) : (
      part
    ),
  );
}

export function ResponseSuggestions({
  suggestions,
  isLoading,
  error,
  onSelectSuggestion,
  onRefresh,
  selectedTone,
  className,
}: ResponseSuggestionsProps) {
  const [showAll, setShowAll] = useState(false);
  const [toneOpen, setToneOpen] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleCopy = async (content: string, index: number) => {
    await navigator.clipboard.writeText(content);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleSelect = (suggestion: SuggestionItem) => {
    onSelectSuggestion?.(suggestion);
  };

  const header = (
    <div className="flex items-center gap-3">
      <span className="co-section-label flex-1">Suggested reply</span>
      {onRefresh ? (
        <div className="relative flex-none">
          <button
            type="button"
            onClick={() => setToneOpen((v) => !v)}
            aria-expanded={toneOpen}
            className="font-mono text-[9.5px] font-medium uppercase tracking-[.12em] text-[var(--co-ink-4)] transition-colors duration-[120ms] hover:text-[var(--co-brass-ink)]"
            title="Change the tone and regenerate"
          >
            Tone · {TONE_LABELS[selectedTone ?? 'professional']}
          </button>
          {toneOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setToneOpen(false)} />
              <div className="absolute right-0 z-20 mt-1 w-[150px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] py-1 shadow-[0_12px_32px_rgba(35,32,28,.14)]">
                {(Object.keys(TONE_LABELS) as ResponseTone[]).map((tone) => (
                  <button
                    key={tone}
                    type="button"
                    onClick={() => {
                      setToneOpen(false);
                      onRefresh(tone);
                    }}
                    className={cn(
                      'block w-full px-3 py-[5px] text-left font-mono text-[10px] uppercase tracking-[.12em] transition-colors duration-[120ms]',
                      tone === selectedTone
                        ? 'bg-[var(--co-brass-wash)] text-[var(--co-brass-ink)]'
                        : 'text-[var(--co-ink-3)] hover:bg-[var(--co-brass-wash)]',
                    )}
                  >
                    {TONE_LABELS[tone]}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        selectedTone && (
          <span className="flex-none font-mono text-[9.5px] font-medium uppercase tracking-[.12em] text-[var(--co-ink-4)]">
            Tone · {TONE_LABELS[selectedTone]}
          </span>
        )
      )}
    </div>
  );

  if (error) {
    return (
      <div className={cn('space-y-[9px]', className)}>
        {header}
        <div className="flex items-center gap-3 rounded-[2px] border border-[var(--co-burgundy)] px-3 py-[10px]">
          <p className="flex-1 text-[12.5px] leading-[1.5] text-[var(--co-burgundy)]">
            Suggestions unavailable.
          </p>
          {onRefresh && (
            <button type="button" className="co-action flex-none" onClick={() => onRefresh()}>
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn('space-y-[9px]', className)}>
        {header}
        {/* Two skeleton tiles at the real dimensions — no spinner. */}
        <div className="flex gap-[9px]">
          {[0, 1].map((i) => (
            <div
              key={i}
              aria-hidden="true"
              className="h-[68px] flex-1 animate-pulse rounded-[2px] border border-[var(--co-line-subtle)] bg-[var(--co-sunken)]"
            />
          ))}
        </div>
      </div>
    );
  }

  if (suggestions.length === 0) {
    return (
      <div className={cn('space-y-[9px]', className)}>
        {header}
        <div className="flex items-center gap-3">
          <p className="flex-1 text-[12.5px] leading-[1.5] text-[var(--co-ink-3)]">
            No suggestions for this ticket yet.
          </p>
          {onRefresh && (
            <button type="button" className="co-action flex-none" onClick={() => onRefresh()}>
              Generate
            </button>
          )}
        </div>
      </div>
    );
  }

  const hidden = suggestions.length - VISIBLE_BY_DEFAULT;
  const visible = showAll ? suggestions : suggestions.slice(0, VISIBLE_BY_DEFAULT);

  return (
    <div className={cn('space-y-[9px]', className)}>
      {header}

      {/* Capped: two clamped tiles sit well under this, so the scrollbar only
          appears once "+N more" is expanded — the composer block keeps a
          predictable height either way. */}
      <div className="flex max-h-[132px] flex-wrap gap-[9px] overflow-y-auto">
        {visible.map((suggestion, index) => (
          <button
            key={index}
            type="button"
            onClick={() => handleSelect(suggestion)}
            className="group min-w-[220px] flex-1 rounded-[2px] border border-[var(--co-line-subtle)] bg-[var(--co-raised)] px-[12px] py-[10px] text-left transition-colors duration-[120ms] hover:border-[var(--co-brass)] hover:bg-[#FFFDF8] dark:hover:bg-[var(--co-raised)]"
          >
            {/* Clamped: a suggestion is a full email, and the composer block
                must not grow at the tab content's expense. Clicking still
                inserts the complete text. */}
            <p
              className="line-clamp-4 overflow-hidden whitespace-pre-wrap text-[12.5px] leading-[1.5] text-[var(--co-ink-2)] [text-wrap:pretty]"
              title={suggestion.content}
            >
              {withReferences(suggestion.content)}
            </p>
            <span className="mt-[7px] flex items-center gap-[10px] font-mono text-[9.5px] uppercase tracking-[.12em] text-[var(--co-ink-4)]">
              <span>{Math.round(suggestion.confidence * 100)}% match</span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  void handleCopy(suggestion.content, index);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    void handleCopy(suggestion.content, index);
                  }
                }}
                className="opacity-0 transition-opacity duration-[120ms] hover:text-[var(--co-brass-ink)] focus:opacity-100 group-hover:opacity-100"
              >
                {copiedIndex === index ? 'Copied' : 'Copy'}
              </span>
            </span>
          </button>
        ))}
      </div>

      {hidden > 0 && (
        <button type="button" className="co-action" onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Show fewer' : `+${hidden} more`}
        </button>
      )}
    </div>
  );
}

// Compact inline version
export function InlineSuggestion({
  suggestion,
  onSelect,
  onCopy,
}: {
  suggestion: SuggestionItem;
  onSelect?: () => void;
  onCopy?: () => void;
}) {
  return (
    <div className="co-chip co-chip-brass w-full gap-3 !normal-case !tracking-normal">
      <p className="flex-1 truncate font-sans text-[12.5px] text-[var(--co-ink-2)]">
        {suggestion.content}
      </p>
      <span className="flex flex-none items-center gap-3">
        {onCopy && (
          <button type="button" className="co-action" onClick={onCopy}>
            Copy
          </button>
        )}
        {onSelect && (
          <button type="button" className="co-action" onClick={onSelect}>
            Use
          </button>
        )}
      </span>
    </div>
  );
}
